import { useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../config';
import { formatLocalDate } from '../utils';
// Wiersze PDF oferty z Historii: jedna funkcja dla okna oferty, „Generuj” i „Wyślij mailem”.
import { historyRows } from '../clientApi';

// Jednostka rabatu zapisanej oferty ('%' albo 'PLN'); bez niej pola nie wysyłamy, a serwer
// zostawia jednostkę zapisaną przy ofercie.
function historyDiscountUnit(offer) {
  const raw = offer && (offer.discount_unit ?? offer.discountUnit);
  if (raw == null || raw === '') return {};
  const unit = String(raw).toUpperCase() === '%' ? '%' : 'PLN';
  return { discount_unit: unit, discountUnit: unit };
}

export default function useOfferActions({
  form,
  dateFrom,
  dateTo,
  chargeableDays,
  selectedEq,
  selectedEquipment,
  discountValue,
  totalPrice,
  toPay,
  advanceAmount,
  totalBail,
  currentOfferId,
  setCurrentOfferId,
  buildOfferData,
  validateOfferInputs,
  resetOfferForm,
  computeOfferDeposit,
  fetchOffersHistory,
  setOffersHistory,
  selectedOffer,
  setSelectedOffer,
  setMessage,
  setError,
  setFieldErrors,
  scrollToTop,
  cancelEditOfferInModal,
  savingOfferModal,
  setSavingOfferModal,
}) {
  const [savingOffer, setSavingOffer] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [showSentOffersModal, setShowSentOffersModal] = useState(false);
  const [sentOffers, setSentOffers] = useState([]);
  const [loadingSentOffers, setLoadingSentOffers] = useState(false);
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false);
  const [showConfirmSendHistoryModal, setShowConfirmSendHistoryModal] = useState(false);
  const [offerToSendFromHistory, setOfferToSendFromHistory] = useState(null);

  const ensureOfferId = async () => {
    if (currentOfferId) return currentOfferId;

    const draftData = buildOfferData({ sendEmail: false });
    const response = await fetch(`${API_BASE}/api/offers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
      credentials: 'include',
      body: JSON.stringify(draftData)
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => null);
      throw new Error(errData?.error || 'Nie udało się zapisać oferty do nadania numeru');
    }

    const result = await response.json().catch(() => ({}));
    const newOfferId = Number(result?.offerId);
    if (!Number.isFinite(newOfferId) || newOfferId <= 0) {
      throw new Error('Nie udało się nadać numeru oferty');
    }

    setCurrentOfferId(newOfferId);
    return newOfferId;
  };

  const handleGenerateOffer = async () => {
    if (!validateOfferInputs()) return;
    try {
      const offerId = await ensureOfferId();
      const offerData = buildOfferData({ offerId });

      const response = await fetch(`${API_BASE}/api/generate-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Błąd generowania oferty');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ln = (form.lastName || '').trim();
      const fn = (form.firstName || '').trim();
      const namePart = [ln, fn].filter(Boolean).join('-');
      a.download = `${namePart}-${new Date().toLocaleDateString('pl-PL').replace(/\./g, '-')}-oferta.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setMessage('Oferta została wygenerowana i pobrana!');
      setError('');
      scrollToTop();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Nie udało się wygenerować oferty';
      setError(msg);
      scrollToTop();
    }
  };

  const handleSaveOffer = async () => {
    if (!validateOfferInputs()) return;
    if (savingOffer) return;
    try {
      setSavingOffer(true);
      setMessage('Zapisywanie oferty...');
      setError('');

      const offerData = buildOfferData({
        sendEmail: false,
        offerId: currentOfferId || undefined
      });

      const response = await fetch(`${API_BASE}/api/offers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || 'Nie udało się zapisać oferty');
      }

      const result = await response.json();
      if (result?.offerId) setCurrentOfferId(result.offerId);
      setMessage(`Oferta zapisana w bazie${result.clientId ? ' (klient dodany/zaktualizowany)' : ''}`);
      scrollToTop();
      try {
        await fetchOffersHistory();
      } catch (e) {
        console.warn('Nie udało się odświeżyć historii ofert po zapisie formularza', e);
      }
      resetOfferForm();
    } catch (err) {
      setMessage('');
      setError(err.message || 'Nie udało się zapisać oferty');
      scrollToTop();
    } finally {
      setSavingOffer(false);
    }
  };

  const fetchSentOffers = async () => {
    setLoadingSentOffers(true);
    try {
      const response = await axios.get(`${API_BASE}/api/sent-offers`, { withCredentials: true });
      setSentOffers(response.data || []);
      setShowSentOffersModal(true);
    } catch {
      setError('Nie udało się pobrać historii wysłanych ofert');
      scrollToTop();
    } finally {
      setLoadingSentOffers(false);
    }
  };

  const handleSendEmailOffer = () => {
    if (!validateOfferInputs()) return;
    setShowConfirmSendModal(true);
  };

  const confirmSendEmail = async () => {
    setShowConfirmSendModal(false);
    if (sendingOffer) return;

    try {
      setSendingOffer(true);
      setMessage('Wysyłanie maila...');
      setError('');

      const offerId = await ensureOfferId();
      const offerData = buildOfferData({ sendEmail: true, offerId });

      const response = await fetch(`${API_BASE}/api/generate-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Błąd wysyłania maila');
      }

      setMessage(`Oferta została zapisana i mail został wysłany na adres: ${form.email}`);
      setError('');
      scrollToTop();
      try {
        await fetchOffersHistory();
      } catch (e) {
        console.warn('Nie udało się odświeżyć historii ofert', e);
      }
      resetOfferForm();
    } catch (err) {
      const msg = err.message || 'Nie udało się wysłać maila z ofertą';
      setError(msg);
      setMessage('');
      const msgLower = msg.toLowerCase();
      const errors = {};
      if (/imię|firstname/.test(msgLower)) errors.firstName = true;
      if (/nazwisko|lastname/.test(msgLower)) errors.lastName = true;
      if (/email/.test(msgLower)) errors.email = true;
      if (/telefon|phone/.test(msgLower)) errors.phoneNumber = true;
      if (/data|date/.test(msgLower)) errors.dateRange = true;
      if (/sprzęt|equipment/.test(msgLower)) errors.equipment = true;
      setFieldErrors(errors);
      scrollToTop();
    } finally {
      setSendingOffer(false);
    }
  };

  const handleGenerateOfferFromHistory = async (offer) => {
    try {
      setMessage('Generowanie oferty...');
      setError('');

      const nameParts = String(offer.client_name || '').trim().split(/\s+/);
      const firstName = offer.firstname || offer.firstName || (nameParts[0] || '');
      const lastName = offer.lastname || offer.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

      const equipRows = await historyRows(offer);

      const offerData = {
        // Numer oferty z historii (test odbiorczy 11.10.2026): bez offerId PDF nie miał numeru,
        // więc różnił się od wysłanego mailem. Bez sendEmail serwer tylko odczytuje numer.
        offerId: offer.id,
        // Kopia wysłanej oferty: serwer daje jej datę wysyłki i ważność liczoną od niej, nie od
        // dziś (test odbiorczy 11.10.2026). Daty z panelu serwer nie czyta.
        kopiaZHistorii: true,
        nazwaKlienta: offer.client_name,
        emailKlienta: offer.email,
        terminOd: new Date(offer.date_from).toLocaleDateString('pl-PL'),
        terminDo: new Date(offer.date_to).toLocaleDateString('pl-PL'),
        equip: equipRows,
        cenaCalosc: parseFloat(offer.subtotal || 0).toFixed(2),
        rabat: parseFloat(offer.discount_amount || 0).toFixed(2),
        ...historyDiscountUnit(offer),
        razemCena: parseFloat(offer.total_price || 0).toFixed(2),
        zaliczka: parseFloat(offer.advance_amount || 0).toFixed(2),
        depozyt: Number(computeOfferDeposit(offer) || 0).toFixed(2),
        firstName,
        lastName,
        email: offer.email,
        // Lokalny dzień: data z bazy przychodzi jako „2026-09-18T22:00:00.000Z" (= 19.09 w Polsce),
        // a toISOString().split('T') cofało ją o dzień w wygenerowanym dokumencie.
        startDate: formatLocalDate(new Date(offer.date_from)),
        endDate: formatLocalDate(new Date(offer.date_to)),
        days: offer.days
      };

      const response = await fetch(`${API_BASE}/api/generate-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Błąd generowania oferty');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${lastName}-${firstName}-${new Date().toLocaleDateString('pl-PL').replace(/\./g, '-')}-oferta.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setMessage('Oferta została wygenerowana i pobrana!');
      setError('');
      scrollToTop();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Nie udało się wygenerować oferty';
      setError(msg);
      scrollToTop();
    }
  };

  const handleSendEmailFromHistory = async (offer) => {
    setShowConfirmSendHistoryModal(false);
    try {
      setMessage('Wysyłanie maila...');
      setError('');

      const nameParts = String(offer.client_name || '').trim().split(/\s+/);
      const firstName = offer.firstname || offer.firstName || (nameParts[0] || '');
      const lastName = offer.lastname || offer.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

      const equipRows = await historyRows(offer);

      const offerData = {
        nazwaKlienta: offer.client_name,
        emailKlienta: offer.email,
        terminOd: new Date(offer.date_from).toLocaleDateString('pl-PL'),
        terminDo: new Date(offer.date_to).toLocaleDateString('pl-PL'),
        equip: equipRows,
        selectedEquipment: offer.equipment || [],
        cenaCalosc: parseFloat(offer.subtotal || 0).toFixed(2),
        rabat: parseFloat(offer.discount_amount || 0).toFixed(2),
        // Jednostka rabatu oferty (test odbiorczy 11.10.2026): bez niej serwer zapisywał po wysyłce
        // rabat 10% jako kwotę w zł, a rezerwacja z oferty dostawała stałą kwotę zamiast procentu.
        ...historyDiscountUnit(offer),
        razemCena: parseFloat(offer.total_price || 0).toFixed(2),
        zaliczka: parseFloat(offer.advance_amount || 0).toFixed(2),
        depozyt: Number(computeOfferDeposit(offer) || 0).toFixed(2),
        firstName,
        lastName,
        email: offer.email,
        startDate: formatLocalDate(new Date(offer.date_from)),
        endDate: formatLocalDate(new Date(offer.date_to)),
        days: offer.days,
        sendEmail: true,
        offerId: offer.id
      };

      const response = await fetch(`${API_BASE}/api/generate-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || 'Nie udało się wysłać maila');
      }

      setSelectedOffer(null);
      scrollToTop();
      setError('');
      setMessage(`Oferta została wysłana na adres: ${offer.email}`);
      await fetchOffersHistory();
    } catch (err) {
      setSelectedOffer(null);
      scrollToTop();
      setMessage('');
      setError(err.message || 'Nie udało się wysłać oferty mailem');
    }
  };

  const saveOfferFromModal = async () => {
    if (!validateOfferInputs()) return;
    if (savingOfferModal) return;

    try {
      setSavingOfferModal(true);
      setMessage('Zapisywanie zmian...');
      setError('');

      const offerData = buildOfferData({
        sendEmail: false,
        offerId: currentOfferId || selectedOffer?.id || undefined
      });

      const response = await fetch(`${API_BASE}/api/offers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || 'Nie udało się zapisać zmian');
      }

      const result = await response.json().catch(() => ({}));
      const savedId = result?.offerId || currentOfferId || selectedOffer?.id;

      const updatedOffer = {
        ...(selectedOffer || {}),
        id: savedId,
        client_name: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
        firstname: form.firstName,
        lastname: form.lastName,
        email: form.email,
        date_from: formatLocalDate(dateFrom),
        date_to: formatLocalDate(dateTo),
        days: chargeableDays,
        subtotal: Number(totalPrice || 0),
        discount_amount: Number(discountValue || 0),
        discount_unit: form.discountUnit,
        total_price: Number(toPay || 0),
        // Liczba z hooka formularza, nie `form.advanceAmount`: to surowy tekst pola (U.26).
        advance_amount: Number(advanceAmount || 0),
        deposit: Number(totalBail || 0),
        equipment: selectedEq,
        equipment_ids: Array.isArray(selectedEquipment) ? selectedEquipment : [],
        // Wiersze właśnie zapisane z oferty: bez tego okno trzymało wiersze sprzed edycji
        // i „Generuj ofertę” zaraz po zapisie drukowało poprzedni sprzęt.
        equipment_rows: offerData.equip,
        // Znacznik „zmieniona po wysyłce” z odpowiedzi serwera (12.10.2026): okno mówi od razu,
        // że kopia z Historii będzie miała dzisiejszą datę.
        changed_after_send_at: Object.prototype.hasOwnProperty.call(result || {}, 'changedAfterSendAt')
          ? result.changedAfterSendAt
          : (selectedOffer?.changed_after_send_at ?? null),
      };

      setOffersHistory(prev => prev.map(o => (o.id === savedId ? updatedOffer : o)));
      setSelectedOffer(updatedOffer);

      setMessage('Zmiany zostały zapisane');
      setError('');
      scrollToTop();

      cancelEditOfferInModal();
      try {
        await fetchOffersHistory();
      } catch (e) {
        console.warn('Nie udało się odświeżyć historii ofert po zapisie', e);
      }
    } catch (err) {
      setMessage('');
      setError(err?.message || 'Nie udało się zapisać zmian');
      scrollToTop();
    } finally {
      setSavingOfferModal(false);
    }
  };

  return {
    savingOffer,
    sendingOffer,
    showSentOffersModal,
    setShowSentOffersModal,
    sentOffers,
    loadingSentOffers,
    showConfirmSendModal,
    setShowConfirmSendModal,
    showConfirmSendHistoryModal,
    setShowConfirmSendHistoryModal,
    offerToSendFromHistory,
    setOfferToSendFromHistory,
    handleGenerateOffer,
    handleSaveOffer,
    fetchSentOffers,
    handleSendEmailOffer,
    confirmSendEmail,
    handleGenerateOfferFromHistory,
    handleSendEmailFromHistory,
    saveOfferFromModal,
  };
}
